import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  type HistoryItem,
  type ItemKind,
  type Project,
  type ResearchCandidate,
  type SearchResult,
  type Tag,
} from "../api/client";
import AttributionBadge from "../components/Attribution";
import ChatStream from "../components/ChatStream";
import { useCurrentProject } from "../hooks/useCurrentProject";

type Filter = "all" | "chat" | "task" | "research";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "chat", label: "Chats" },
  { key: "task", label: "Tasks" },
  { key: "research", label: "Research" },
];

function formatDate(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: sameYear ? undefined : "numeric",
  });
}

function itemKey(it: HistoryItem): string {
  return `${it.kind}-${it.id}`;
}

function renderExcerpt(html: string): React.ReactNode {
  const parts = html.split(/<\/?mark>/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>,
  );
}

export default function HistoryPage() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [tagFilter, setTagFilter] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [project] = useCurrentProject();
  const navigate = useNavigate();

  const projectFilterDescription = useMemo(() => {
    if (project.kind === "all") return null;
    if (project.kind === "inbox") return "Inbox";
    return projects.find((p) => p.id === project.id)?.name ?? `Study ${project.id}`;
  }, [project, projects]);

  function fetchHistory() {
    setLoading(true);
    setError(null);
    const opts: Parameters<typeof api.listHistory>[0] = {};
    if (project.kind === "inbox") opts.inbox = true;
    else if (project.kind === "project") opts.project_id = project.id;
    if (tagFilter != null) opts.tag_id = tagFilter;
    api
      .listHistory(opts)
      .then(setItems)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.kind, project.kind === "project" ? project.id : "", tagFilter]);

  useEffect(() => {
    api.listProjects().then(setProjects).catch(() => {});
    api.listTags().then(setTags).catch(() => {});
  }, []);

  async function runSearch() {
    const q = searchInput.trim();
    if (!q) {
      setActiveQuery("");
      setSearchResults(null);
      return;
    }
    setActiveQuery(q);
    setSearching(true);
    setError(null);
    try {
      const resp = await api.search(q);
      setSearchResults(resp.results);
    } catch (e) {
      setError(String(e));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchInput("");
    setActiveQuery("");
    setSearchResults(null);
  }

  async function handleDelete(it: HistoryItem) {
    const label =
      it.kind === "chat" ? `conversation "${it.title}"` :
      it.kind === "task" ? `task run "${it.title}"` :
      `research query "${it.title}"`;
    if (!window.confirm(`Delete this ${label}?\n\nThis can't be undone.`)) return;
    try {
      if (it.kind === "chat") await api.deleteThread(it.id);
      else if (it.kind === "task") await api.deleteTaskRun(it.id);
      else await api.deleteResearchRun(it.id);
      setItems((items) => items.filter((x) => !(x.kind === it.kind && x.id === it.id)));
      setExpanded(null);
    } catch (e) {
      alert(`Couldn't delete: ${(e as Error).message}`);
    }
  }

  async function handleSetProject(it: HistoryItem, newPid: number | null) {
    try {
      await api.setItemProject(it.kind as ItemKind, it.id, newPid);
      setItems((items) =>
        items.map((x) =>
          x.kind === it.kind && x.id === it.id ? { ...x, project_id: newPid } : x,
        ),
      );
    } catch (e) {
      alert(`Couldn't move: ${(e as Error).message}`);
    }
  }

  async function handleAddTag(it: HistoryItem, tagName: string) {
    const name = tagName.trim();
    if (!name) return;
    try {
      let tag = tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (!tag) {
        tag = await api.createTag(name);
        setTags((ts) => [...ts, tag!]);
      }
      await api.addItemTag(it.kind as ItemKind, it.id, tag.id);
      setItems((items) =>
        items.map((x) => {
          if (x.kind !== it.kind || x.id !== it.id) return x;
          if (x.tags.some((t) => t.id === tag!.id)) return x;
          return { ...x, tags: [...x.tags, { id: tag!.id, name: tag!.name, color: tag!.color }] };
        }),
      );
    } catch (e) {
      alert(`Couldn't add tag: ${(e as Error).message}`);
    }
  }

  async function handleRemoveTag(it: HistoryItem, tagId: number) {
    try {
      await api.removeItemTag(it.kind as ItemKind, it.id, tagId);
      setItems((items) =>
        items.map((x) =>
          x.kind === it.kind && x.id === it.id
            ? { ...x, tags: x.tags.filter((t) => t.id !== tagId) }
            : x,
        ),
      );
    } catch (e) {
      alert(`Couldn't remove tag: ${(e as Error).message}`);
    }
  }

  const visible = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.kind === filter);
  }, [items, filter]);

  const filteredResults = useMemo(() => {
    if (searchResults == null) return null;
    if (filter === "all") return searchResults;
    return searchResults.filter((r) => r.kind === filter);
  }, [searchResults, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, chat: 0, task: 0, research: 0 };
    for (const i of items) c[i.kind]++;
    return c;
  }, [items]);

  const inSearchMode = searchResults !== null && activeQuery !== "";

  return (
    <>
      <div className="main-header" style={{ flexWrap: "wrap", gap: 12 }}>
        <h2>History</h2>
        <form
          className="search-form"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        >
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search chats, tasks, library…"
            aria-label="Search history"
          />
          <button type="submit" className="primary" disabled={searching}>
            {searching ? <><span className="spinner" />Searching</> : "Search"}
          </button>
          {inSearchMode && (
            <button type="button" className="ghost" onClick={clearSearch}>
              Clear
            </button>
          )}
        </form>
        <span className="spacer" />
        <div className="filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {!inSearchMode && <span className="count">{counts[f.key]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="main-body">
        {(projectFilterDescription || tagFilter != null || tags.length > 0) && (
          <div className="history-filter-row">
            {projectFilterDescription && (
              <span className="filter-pill">
                Study: <strong>{projectFilterDescription}</strong>
                <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
                  (change in sidebar)
                </span>
              </span>
            )}
            {tags.length > 0 && (
              <>
                <span className="muted" style={{ fontSize: 12 }}>Tag:</span>
                <button
                  type="button"
                  className={`chip ${tagFilter == null ? "active" : ""}`}
                  onClick={() => setTagFilter(null)}
                >
                  any
                </button>
                {tags.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`chip ${tagFilter === t.id ? "active" : ""}`}
                    onClick={() => setTagFilter(tagFilter === t.id ? null : t.id)}
                    style={t.color ? { borderColor: t.color } : undefined}
                  >
                    <span
                      className="tag-dot"
                      style={{ background: t.color || "var(--accent)" }}
                    />
                    {t.name}
                    {t.item_count > 0 && <span className="count">{t.item_count}</span>}
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        {inSearchMode ? (
          <SearchResultsView
            query={activeQuery}
            results={filteredResults ?? []}
            navigate={navigate}
          />
        ) : (
          <BrowseView
            loading={loading}
            visible={visible}
            filter={filter}
            expanded={expanded}
            setExpanded={setExpanded}
            navigate={navigate}
            projects={projects}
            tags={tags}
            onDelete={handleDelete}
            onSetProject={handleSetProject}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
          />
        )}
      </div>
    </>
  );
}

function SearchResultsView({
  query, results, navigate,
}: { query: string; results: SearchResult[]; navigate: ReturnType<typeof useNavigate> }) {
  if (results.length === 0) {
    return (
      <p className="muted">
        No matches for <strong>{query}</strong>. Try fewer or different words.
      </p>
    );
  }
  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        {results.length} match{results.length === 1 ? "" : "es"} for <strong>{query}</strong>
      </p>
      <div className="history-list">
        {results.map((r, i) => (
          <div
            key={`${r.kind}-${r.ref_id}-${i}`}
            className={`history-card ${r.kind}`}
            onClick={() => {
              if (r.kind === "chat") navigate(`/chat/${r.ref_id}`);
              else if (r.kind === "library") navigate(`/library/${r.ref_id}`);
            }}
            style={{ cursor: r.kind === "chat" || r.kind === "library" ? "pointer" : "default" }}
          >
            <div className="history-head">
              <span className={`kind-badge ${r.kind}`}>{r.kind}</span>
              <div className="history-text">
                <div className="history-title">{r.title}</div>
                <div className="history-preview search-excerpt">{renderExcerpt(r.excerpt)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

interface BrowseViewProps {
  loading: boolean;
  visible: HistoryItem[];
  filter: Filter;
  expanded: string | null;
  setExpanded: (k: string | null) => void;
  navigate: ReturnType<typeof useNavigate>;
  projects: Project[];
  tags: Tag[];
  onDelete: (it: HistoryItem) => void;
  onSetProject: (it: HistoryItem, project_id: number | null) => void;
  onAddTag: (it: HistoryItem, name: string) => void;
  onRemoveTag: (it: HistoryItem, tag_id: number) => void;
}

function BrowseView({
  loading, visible, filter, expanded, setExpanded, navigate,
  projects, tags, onDelete, onSetProject, onAddTag, onRemoveTag,
}: BrowseViewProps) {
  if (loading) return <p className="muted">Loading…</p>;
  if (visible.length === 0) {
    return (
      <p className="muted">
        {filter === "all"
          ? "No items match the current filters."
          : `No ${filter} items match the current filters.`}
      </p>
    );
  }
  return (
    <div className="history-list">
      {visible.map((it) => {
        const key = itemKey(it);
        const isOpen = expanded === key;
        const project = projects.find((p) => p.id === it.project_id);
        return (
          <div key={key} className={`history-card ${it.kind}`}>
            <div
              className="history-head"
              onClick={() => {
                if (it.kind === "chat") navigate(`/chat/${it.id}`);
                else setExpanded(isOpen ? null : key);
              }}
            >
              <span className={`kind-badge ${it.kind}`}>{it.kind}</span>
              <div className="history-text">
                <div className="history-title">{it.title}</div>
                {it.preview && <div className="history-preview">{it.preview}</div>}
                {(project || it.tags.length > 0 || it.created_by) && (
                  <div className="history-chips">
                    <AttributionBadge createdBy={it.created_by} compact />
                    {project && (
                      <span
                        className="project-chip"
                        style={{ borderColor: project.color || "var(--accent)" }}
                      >
                        <span
                          className="project-dot"
                          style={{ background: project.color || "var(--accent)" }}
                        />
                        {project.name}
                      </span>
                    )}
                    {it.tags.map((t) => (
                      <span
                        key={t.id}
                        className="tag-chip"
                        style={t.color ? { borderColor: t.color } : undefined}
                      >
                        <span
                          className="tag-dot"
                          style={{ background: t.color || "var(--accent)" }}
                        />
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="history-meta">
                {it.kind === "chat" && (
                  <span className="muted">
                    {it.message_count} msg{it.message_count === 1 ? "" : "s"}
                    {it.archived ? " · archived" : ""}
                  </span>
                )}
                {it.kind === "research" && (
                  <span className="muted">
                    {it.candidate_count} candidate{it.candidate_count === 1 ? "" : "s"}
                    {it.approved_count ? ` · ${it.approved_count} ingested` : ""}
                  </span>
                )}
                <span className="muted">
                  {formatDate(it.kind === "chat" ? it.updated_at : it.created_at)}
                </span>
                <div className="card-controls" onClick={(e) => e.stopPropagation()}>
                  <select
                    className="project-mover"
                    value={it.project_id ?? ""}
                    onChange={(e) => {
                      onSetProject(it, e.target.value === "" ? null : Number(e.target.value));
                    }}
                    title="Move to study"
                  >
                    <option value="">→ Inbox</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>→ {p.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="card-delete"
                    onClick={() => onDelete(it)}
                    title="Delete this item"
                    aria-label="Delete"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            {isOpen && it.kind === "task" && (
              <TaskDetail
                item={it}
                projects={projects}
                tags={tags}
                onDelete={() => onDelete(it)}
                onSetProject={(pid) => onSetProject(it, pid)}
                onAddTag={(name) => onAddTag(it, name)}
                onRemoveTag={(tid) => onRemoveTag(it, tid)}
              />
            )}
            {isOpen && it.kind === "research" && (
              <ResearchDetail
                item={it}
                projects={projects}
                tags={tags}
                onDelete={() => onDelete(it)}
                onSetProject={(pid) => onSetProject(it, pid)}
                onAddTag={(name) => onAddTag(it, name)}
                onRemoveTag={(tid) => onRemoveTag(it, tid)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface DetailActionsProps {
  item: HistoryItem;
  projects: Project[];
  tags: Tag[];
  onSetProject: (project_id: number | null) => void;
  onAddTag: (name: string) => void;
  onRemoveTag: (tag_id: number) => void;
}

function ProjectTagBar({ item, projects, tags, onSetProject, onAddTag, onRemoveTag }: DetailActionsProps) {
  const [tagInput, setTagInput] = useState("");
  const [tagInputOpen, setTagInputOpen] = useState(false);
  return (
    <div className="history-detail-controls">
      <label className="muted" style={{ fontSize: 12 }}>
        Study:&nbsp;
        <select
          value={item.project_id ?? ""}
          onChange={(e) => onSetProject(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">Inbox</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
      <div className="tag-edit-row">
        <span className="muted" style={{ fontSize: 12 }}>Tags:</span>
        {item.tags.map((t) => (
          <span
            key={t.id}
            className="tag-chip removable"
            style={t.color ? { borderColor: t.color } : undefined}
          >
            <span className="tag-dot" style={{ background: t.color || "var(--accent)" }} />
            {t.name}
            <button
              type="button"
              className="tag-remove"
              aria-label={`Remove tag ${t.name}`}
              onClick={() => onRemoveTag(t.id)}
            >
              ×
            </button>
          </span>
        ))}
        {tagInputOpen ? (
          <input
            autoFocus
            list={`tag-suggestions-${item.kind}-${item.id}`}
            className="tag-input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onAddTag(tagInput);
                setTagInput("");
                setTagInputOpen(false);
              } else if (e.key === "Escape") {
                setTagInput("");
                setTagInputOpen(false);
              }
            }}
            onBlur={() => {
              if (tagInput.trim()) onAddTag(tagInput);
              setTagInput("");
              setTagInputOpen(false);
            }}
            placeholder="tag name…"
          />
        ) : (
          <button type="button" className="ghost-link" onClick={() => setTagInputOpen(true)}>
            + tag
          </button>
        )}
        <datalist id={`tag-suggestions-${item.kind}-${item.id}`}>
          {tags.map((t) => <option key={t.id} value={t.name} />)}
        </datalist>
      </div>
    </div>
  );
}

function TaskDetail({
  item, projects, tags, onDelete, onSetProject, onAddTag, onRemoveTag,
}: {
  item: Extract<HistoryItem, { kind: "task" }>;
  projects: Project[];
  tags: Tag[];
  onDelete: () => void;
  onSetProject: (project_id: number | null) => void;
  onAddTag: (name: string) => void;
  onRemoveTag: (tag_id: number) => void;
}) {
  return (
    <div className="history-detail">
      <AttributionBadge createdBy={item.created_by} />
      {Object.keys(item.inputs).length > 0 && (
        <details open>
          <summary>Inputs</summary>
          <dl className="kv">
            {Object.entries(item.inputs).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{typeof v === "string" ? v : JSON.stringify(v)}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      {item.result_text ? (
        <details open>
          <summary>Result</summary>
          <div className="task-result">
            <ChatStream content={item.result_text} citations={[]} />
          </div>
        </details>
      ) : (
        <p className="muted">No result text recorded for this run.</p>
      )}
      <ProjectTagBar
        item={item}
        projects={projects}
        tags={tags}
        onSetProject={onSetProject}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
      />
      <div className="history-detail-actions">
        <a className="ghost-link" href={`/api/tasks/runs/${item.id}/export.md`}>
          Export as Markdown
        </a>
        <button type="button" className="danger-link" onClick={onDelete}>
          Delete this task run
        </button>
      </div>
    </div>
  );
}

function ResearchDetail({
  item, projects, tags, onDelete, onSetProject, onAddTag, onRemoveTag,
}: {
  item: Extract<HistoryItem, { kind: "research" }>;
  projects: Project[];
  tags: Tag[];
  onDelete: () => void;
  onSetProject: (project_id: number | null) => void;
  onAddTag: (name: string) => void;
  onRemoveTag: (tag_id: number) => void;
}) {
  const approvedSet = new Set(item.approved_source_ids);
  return (
    <div className="history-detail">
      <AttributionBadge createdBy={item.created_by} />
      <p className="muted" style={{ marginTop: 0 }}>
        Query: <strong>{item.preview}</strong>
      </p>
      {item.candidates.length === 0 ? (
        <p className="muted">No candidates returned for this query.</p>
      ) : (
        <ul className="candidate-list">
          {item.candidates.map((c: ResearchCandidate, idx: number) => (
            <li key={`${item.id}-${idx}`}>
              <div className="title">{c.title}</div>
              {(c.authors || c.year) && (
                <div className="meta">
                  {c.authors}
                  {c.authors && c.year ? " · " : ""}
                  {c.year || ""}
                </div>
              )}
              {c.url && (
                <div className="url">
                  <a href={c.url} target="_blank" rel="noreferrer">{c.url}</a>
                </div>
              )}
              {c.abstract && <div style={{ marginTop: 4 }}>{c.abstract}</div>}
              {c.why_relevant && (
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  Why relevant: {c.why_relevant}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {approvedSet.size > 0 && (
        <p className="muted" style={{ marginTop: 8 }}>
          {approvedSet.size} of these were ingested into the library.
        </p>
      )}
      <ProjectTagBar
        item={item}
        projects={projects}
        tags={tags}
        onSetProject={onSetProject}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
      />
      <div className="history-detail-actions">
        <a className="ghost-link" href={`/api/research/runs/${item.id}/export.md`}>
          Export as Markdown
        </a>
        <button type="button" className="danger-link" onClick={onDelete}>
          Delete this research run
        </button>
      </div>
    </div>
  );
}
